// Anchored map popover, rendered in the LIGHT DOM.
//
// Two things rule out the obvious approaches:
//
// 1. `google.maps.InfoWindow` imposes its own chrome, close button, tail and
//    width clamp, and its inner container cannot be reached by app CSS.
// 2. Appending to an OverlayView pane does not work either — `<gmp-map>` keeps
//    its panes inside a shadow root, so Tailwind/DaisyUI classes do not cascade
//    in and the content renders unstyled.
//
// So the popover element lives in the light DOM next to the map, and an
// OverlayView is used purely as a projection tick: its `draw()` fires on every
// pan/zoom, and we translate the anchor LatLng into pixels ourselves.

export interface MapPopover {
  close(): void
  readonly element: HTMLElement
}

// OverlayView only exists once the Maps SDK has loaded, so the subclass cannot
// be declared at module scope — it is built on first use.
let OverlayCtor: any = null

function overlayCtor() {
  if (OverlayCtor) return OverlayCtor

  OverlayCtor = class extends google.maps.OverlayView {
    onDraw: (projection: google.maps.MapCanvasProjection) => void
    private probe = document.createElement("div")

    constructor(onDraw: (projection: google.maps.MapCanvasProjection) => void) {
      super()
      this.onDraw = onDraw
    }

    // A zero-size probe is still required: OverlayView only receives draw()
    // calls while it is attached to a pane.
    onAdd() {
      this.getPanes()?.overlayLayer.appendChild(this.probe)
    }

    onRemove() {
      this.probe.remove()
    }

    draw() {
      const projection = this.getProjection()
      if (projection) this.onDraw(projection)
    }
  }

  return OverlayCtor
}

interface PopoverOptions {
  // Light-DOM element the popover is positioned within. Must be `position:
  // relative`. The map element must be inside it.
  anchor: HTMLElement
  mapElement: HTMLElement
  // Vertical clearance so the popover clears the marker pin.
  offsetY?: number
}

export function openMapPopover(
  map: google.maps.Map,
  position: google.maps.LatLngLiteral,
  content: HTMLElement,
  { anchor, mapElement, offsetY = 46 }: PopoverOptions
): MapPopover {
  const container = document.createElement("div")
  container.className = "absolute z-20 -translate-x-1/2 -translate-y-full pointer-events-auto"
  container.style.visibility = "hidden"
  container.appendChild(content)
  anchor.appendChild(container)

  const reposition = (projection: google.maps.MapCanvasProjection) => {
    const point = projection.fromLatLngToContainerPixel(new google.maps.LatLng(position))
    if (!point) return

    const mapRect = mapElement.getBoundingClientRect()
    const anchorRect = anchor.getBoundingClientRect()

    container.style.left = `${mapRect.left - anchorRect.left + point.x}px`
    container.style.top = `${mapRect.top - anchorRect.top + point.y - offsetY}px`
    container.style.visibility = "visible"
  }

  const overlay = new (overlayCtor())(reposition)
  overlay.setMap(map)

  let closed = false
  const close = () => {
    if (closed) return
    closed = true
    document.removeEventListener("keydown", onKeydown)
    mapClick.remove()
    overlay.setMap(null)
    container.remove()
  }

  const onKeydown = (event: KeyboardEvent) => {
    if (event.key === "Escape") close()
  }

  document.addEventListener("keydown", onKeydown)
  const mapClick = map.addListener("click", close)

  content.addEventListener("click", (event) => {
    if ((event.target as HTMLElement).closest("[data-popover-close]")) close()
  })

  return { close, element: container }
}
